import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  describeAuthError,
  isSessionExpiredError,
  noteTokenCache,
  cachedTokenHadExpired,
  reportAuthEvent,
  useAuth,
  useRealtimeSync,
} from '@structural-climbing/auth'
import { Button, Spinner } from '@structural-climbing/ui'
import { takeChildInvite } from '../features/family/pendingChildInvite'

interface PendingRequest {
  id: string
  status: 'pending' | 'approved'
  gym_name: string | null
  created_at: string
}

interface SubscriptionEntry {
  gym: { id: string; name: string; slug: string }
}

type PageState =
  | { kind: 'loading' }
  | { kind: 'pending'; request: PendingRequest }
  | { kind: 'approved'; request: PendingRequest }
  /** A child's own login with no active subscription at any gym. */
  | { kind: 'childNoPlan' }
  /** Signed up with the address a parent invited, but without the invite link. */
  | { kind: 'childInvitePending' }

export function GymRedirect() {
  const { getAccessTokenSilently } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [state, setState] = useState<PageState>({ kind: 'loading' })

  // A member with no gym belongs on Our gyms, which is where joining lives.
  const goDiscover = useCallback(() => {
    void navigate('/our-gyms', { replace: true })
  }, [navigate])

  // A dead session and a dead network both surface here as a rejected token
  // call, and they need opposite answers. Sending an expired member to Our gyms
  // told them they belong to no gym, which is a lie, and then RequireRole
  // bounced them to /login anyway — so they watched a screen they did not ask
  // for flash past on the way. Send them straight there instead.
  const goSignIn = useCallback(() => {
    void navigate('/login', { state: { returnTo: '/' }, replace: true })
  }, [navigate])

  // One silent-token failure is not proof the session is gone. The cached entry
  // can be unusable for reasons a forced renewal fixes, and treating the first
  // rejection as terminal is what put a brand-new member into a sign-in loop:
  // this page sent them to /login, /login bounced them through Auth0, Auth0's
  // SSO session answered immediately, and the same call failed again. Ask once
  // more with the cache off, and only then give up.
  const tokenWithRetry = useCallback(async () => {
    // A failure wipes the SDK cache, so look at it first.
    noteTokenCache()
    try {
      return await getAccessTokenSilently()
    } catch (e) {
      if (!isSessionExpiredError(e)) throw e
      // A token that had already run out and could not be renewed is a session
      // that ended while the member was away: worth counting, not a warning.
      // Keep the warning for a failure with a valid token in the cache, which
      // is what the iOS sign-in bug looked like.
      const expired = cachedTokenHadExpired()
      reportAuthEvent(
        expired ? 'auth.session_expired' : 'auth.silent_token_failed',
        { ...describeAuthError(e), where: 'GymRedirect', retrying: true },
        expired ? 'info' : 'warning',
      )
      return await getAccessTokenSilently({ cacheMode: 'off' })
    }
  }, [getAccessTokenSilently])

  const resolve = useCallback(async () => {
    try {
      const token = await tokenWithRetry()
      const [subRes, profileRes] = await Promise.all([
        fetch(`${apiUrl}/my-gym-memberships`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiUrl}/my-profile`, { headers: { Authorization: `Bearer ${token}` } }),
      ])

      const subData = subRes.ok
        ? ((await subRes.json()) as {
            memberships: SubscriptionEntry[]
            child_account?: unknown
            pending_child_invite?: boolean
          })
        : { memberships: [] }
      const profileData = profileRes.ok
        ? ((await profileRes.json()) as { preferredGymId?: string | null })
        : { preferredGymId: null }

      const memberships = subData.memberships
      if (memberships.length > 0) {
        const preferredGymId = profileData.preferredGymId ?? null
        let targetSlug = memberships[0].gym.slug
        if (preferredGymId) {
          const preferred = memberships.find((s) => s.gym.id === preferredGymId)
          if (preferred) targetSlug = preferred.gym.slug
        }
        void navigate(`/${targetSlug}/dashboard`, { replace: true })
        return
      }

      // A child's own login with no gym: the child has no active subscription
      // anywhere. They cannot join a gym or buy a plan themselves, so there is
      // nowhere useful to send them. Say what is missing and who can fix it.
      if (subData.child_account) {
        setState({ kind: 'childNoPlan' })
        return
      }

      // A child who opened their invite in this browser and then arrived here
      // without it: a second tab, the link in another email, a sign-out on the
      // way. Take them back to it; the invite page does the rest.
      const inviteToken = takeChildInvite()
      if (inviteToken) {
        void navigate(`/child-invite?token=${encodeURIComponent(inviteToken)}`, { replace: true })
        return
      }

      // The same child on a browser that never saw the link. Joining a gym is
      // where its documents are signed, and that is the parent's to do, so say
      // where the invite is and go no further.
      if (subData.pending_child_invite) {
        setState({ kind: 'childInvitePending' })
        return
      }

      // No subscription — check for a pending join request
      const joinRes = await fetch(`${apiUrl}/my-join-request`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (joinRes.ok) {
        const joinData = (await joinRes.json()) as { request: PendingRequest | null }
        if (joinData.request) {
          const req = joinData.request
          setState(
            req.status === 'approved'
              ? { kind: 'approved', request: req }
              : { kind: 'pending', request: req },
          )
          return
        }
        const returnTo = sessionStorage.getItem('auth_return_to')
        if (returnTo) {
          sessionStorage.removeItem('auth_return_to')
          void navigate(returnTo)
          return
        }
      }
      goDiscover()
    } catch (e) {
      if (isSessionExpiredError(e)) {
        reportAuthEvent('auth.session_lost', {
          ...describeAuthError(e),
          where: 'GymRedirect',
          retrying: false,
        })
        goSignIn()
        return
      }
      goDiscover()
    }
  }, [tokenWithRetry, navigate, apiUrl, goDiscover, goSignIn])

  useEffect(() => {
    void resolve()
  }, [resolve])

  const requestId = state.kind === 'pending' ? state.request.id : null

  const bindings = useMemo(
    () =>
      requestId
        ? [{ table: 'join_requests', event: 'UPDATE' as const, filter: `id=eq.${requestId}` }]
        : [],
    [requestId],
  )

  useRealtimeSync({
    channel: requestId ? `join-request-${requestId}` : null,
    bindings,
    onChange: () => void resolve(),
    onResync: () => void resolve(),
  })

  if (state.kind === 'loading') {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size="lg" color="primary" />
      </div>
    )
  }

  if (state.kind === 'childNoPlan') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-warning/10 text-3xl">
          <span aria-hidden>🧗</span>
        </div>
        <p className="text-xl font-bold">{t('childProfile.noPlanTitle')}</p>
        <p className="max-w-xs text-sm text-foreground/60">{t('childProfile.noGym')}</p>
        <Button as={Link} to="/competitions" variant="flat">
          {t('competitions.menuLink')}
        </Button>
      </div>
    )
  }

  if (state.kind === 'childInvitePending') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-warning/10 text-3xl">
          <span aria-hidden>✉️</span>
        </div>
        <p className="text-xl font-bold">{t('childInvite.pendingTitle')}</p>
        <p className="max-w-xs text-sm text-foreground/60">{t('childInvite.pendingBody')}</p>
      </div>
    )
  }

  if (state.kind === 'pending' || state.kind === 'approved') {
    const req = state.request
    const isApproved = state.kind === 'approved'
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
        <div
          className={`flex h-16 w-16 items-center justify-center rounded-full text-3xl ${isApproved ? 'bg-success/10' : 'bg-warning/10'}`}
        >
          {isApproved ? '✓' : '⏳'}
        </div>
        <div>
          <p className="text-xl font-bold">
            {isApproved ? t('dashboard.approved') : t('dashboard.waitingApproval')}
          </p>
          {req.gym_name && (
            <p className="mt-1 text-sm text-foreground/60">
              {isApproved
                ? t('dashboard.approvedAt', { gymName: req.gym_name })
                : t('dashboard.pendingAt', { gymName: req.gym_name })}
            </p>
          )}
          <p className="mt-3 text-xs text-foreground/40">
            {t('dashboard.submitted', {
              date: new Date(req.created_at).toLocaleDateString(undefined, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              }),
            })}
          </p>
        </div>
        <p className="max-w-xs text-sm text-foreground/50">
          {isApproved ? t('dashboard.approvedDetail') : t('dashboard.pendingDetail')}
        </p>
      </div>
    )
  }

  // Every other outcome navigates away, so there is nothing left to draw.
  return null
}
